import { Card, Input, Select, Toggle, Textarea, TagsInput } from '../shared';

const AGENT_TYPES = ['default', 'partner', 'voice', 'specialized', 'user_created'];
const OWNER_TYPES = ['system', 'partner', 'user'];
const REASONING_EFFORTS = ['low', 'medium', 'high'];
const BRAND_ICONS = ['nebula', 'simplize'];
const SESSION_TYPES = ['hub', 'chat'];

export default function TabGeneral({ formData, handleChange, isCreate, isSubAgent = false, environments = [] }) {
    return (
        <div className="space-y-4">
            <Card title="Identity">
                {isCreate && (
                    <Input label="ID" hint="Auto-generated if empty" value={formData.id || ''} onChange={(e) => handleChange('id', e.target.value)} placeholder="my-agent-id" />
                )}
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required />
                    <Select label="Type" options={AGENT_TYPES} value={formData.agent_type} onChange={(e) => handleChange('agent_type', e.target.value)} />
                </div>
                <Textarea label="Description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} rows={2} />
                <div className="grid grid-cols-2 gap-3">
                    <Select label="Owner Type" options={OWNER_TYPES} value={formData.owner_type} onChange={(e) => handleChange('owner_type', e.target.value)} />
                    <Input label="Owner ID" value={formData.owner_id || ''} onChange={(e) => handleChange('owner_id', e.target.value)} />
                </div>
                <Toggle label="Public" checked={formData.is_public} onChange={(val) => handleChange('is_public', val)} />
                {isSubAgent && (
                    <div className="mt-3 space-y-3">
                        <Select
                            label="Environment"
                            value={formData.environment || ''}
                            options={[{ value: '', label: '— default env —' }, ...environments.map(e => ({ value: e.id, label: `${e.id}${e.is_default ? ' (default)' : ''}` }))]}
                            onChange={(e) => handleChange('environment', e.target.value || null)}
                        />
                        <TagsInput
                            label="Tags"
                            helpText="Parent agents can opt-in this subagent by tag via subagents.include_tags."
                            value={formData.tags || []}
                            onChange={(tags) => handleChange('tags', tags)}
                        />
                    </div>
                )}
            </Card>

            <Card title="Model & Reasoning">
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Model" value={formData.model_name} onChange={(e) => handleChange('model_name', e.target.value)} />
                    <Input label="Temperature" type="number" step="0.1" min="0" max="2" value={formData.temperature} onChange={(e) => handleChange('temperature', parseFloat(e.target.value))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Max Tokens" hint="Leave empty for default" type="number" min="1" value={formData.max_tokens || ''} onChange={(e) => handleChange('max_tokens', e.target.value ? parseInt(e.target.value) : null)} placeholder="Default" />
                    <Input label="Max Response Tokens" type="number" min="1" value={formData.max_response_tokens || ''} onChange={(e) => handleChange('max_response_tokens', e.target.value ? parseInt(e.target.value) : null)} placeholder="Default" />
                </div>
                <div className="flex items-end gap-4">
                    <Toggle label="Reasoning" checked={formData.reasoning_enabled} onChange={(val) => handleChange('reasoning_enabled', val)} />
                    {formData.reasoning_enabled && (
                        <Select label="Effort" options={REASONING_EFFORTS} value={formData.reasoning_effort || 'medium'} onChange={(e) => handleChange('reasoning_effort', e.target.value)} className="w-32" />
                    )}
                </div>
            </Card>

            <Card title="Voice">
                <div className="flex items-center gap-4">
                    <Toggle label="Voice" checked={formData.voice_enabled === true} onChange={(val) => handleChange('voice_enabled', val)} />
                    {formData.voice_enabled && (
                        <>
                            <Input label="Model" value={formData.voice_model || ''} onChange={(e) => handleChange('voice_model', e.target.value || null)} placeholder="e.g. alloy" className="flex-1" />
                            <Input label="Speed" type="number" step="0.1" min="0.5" max="2" value={formData.voice_speed || 1.0} onChange={(e) => handleChange('voice_speed', parseFloat(e.target.value))} className="w-20" />
                        </>
                    )}
                </div>
            </Card>

            <BrandSection formData={formData} handleChange={handleChange} />
        </div>
    );
}

function BrandSection({ formData, handleChange }) {
    const brand = formData.brand || {};
    const defaultBrand = brand.default || {};
    const sessionTypes = brand.session_types || {};
    const overrides = Object.entries(sessionTypes).map(([type, config]) => ({ session_type: type, ...config }));

    const updateBrand = (newBrand) => {
        const cleaned = { ...newBrand };
        if (!cleaned.default?.name) delete cleaned.default;
        if (!cleaned.session_types || Object.keys(cleaned.session_types).length === 0) delete cleaned.session_types;
        handleChange('brand', Object.keys(cleaned).length > 0 ? cleaned : null);
    };

    const updateDefault = (field, value) => {
        updateBrand({ ...brand, default: { ...defaultBrand, [field]: value } });
    };

    const updateOverride = (index, field, value) => {
        const entries = [...overrides];
        entries[index] = { ...entries[index], [field]: value };
        const newSessionTypes = {};
        entries.forEach(e => {
            if (e.session_type && e.name) {
                newSessionTypes[e.session_type] = { name: e.name, icon: e.icon || 'nebula', color: e.color || '#4D4D4D' };
            }
        });
        updateBrand({ ...brand, session_types: newSessionTypes });
    };

    const addOverride = () => {
        const usedTypes = overrides.map(o => o.session_type);
        const available = SESSION_TYPES.find(t => !usedTypes.includes(t));
        if (!available) return;
        const newEntry = { session_type: available, name: available === 'hub' ? 'Nebula' : 'Simplize AI', icon: 'nebula', color: '#4D4D4D' };
        const newSessionTypes = { ...sessionTypes, [available]: { name: newEntry.name, icon: newEntry.icon, color: newEntry.color } };
        updateBrand({ ...brand, session_types: newSessionTypes });
    };

    const removeOverride = (index) => {
        const entries = [...overrides];
        entries.splice(index, 1);
        const newSessionTypes = {};
        entries.forEach(e => {
            if (e.session_type && e.name) {
                newSessionTypes[e.session_type] = { name: e.name, icon: e.icon || 'nebula', color: e.color || '#4D4D4D' };
            }
        });
        updateBrand({ ...brand, session_types: Object.keys(newSessionTypes).length > 0 ? newSessionTypes : undefined });
    };

    return (
        <Card title="Brand Display">
            <p className="text-xs text-gray-500 mb-3">Avatar + name header on AI messages. Leave empty to disable.</p>

            <div className="border border-gray-200 rounded-lg p-3 mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-2">Default (all sessions)</label>
                <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Brand name" value={defaultBrand.name || ''} onChange={(e) => updateDefault('name', e.target.value)} />
                    <Select label="" options={['', ...BRAND_ICONS]} value={defaultBrand.icon || ''} onChange={(e) => updateDefault('icon', e.target.value)} />
                    <div className="flex items-center gap-2">
                        <input type="color" value={defaultBrand.color || '#4D4D4D'} onChange={(e) => updateDefault('color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                        <span className="text-xs text-gray-400">{defaultBrand.color || '#4D4D4D'}</span>
                    </div>
                </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-600">Session Type Overrides</label>
                    <button type="button" onClick={addOverride} className="text-xs text-blue-600 hover:text-blue-800">+ Add</button>
                </div>
                {overrides.length === 0 && (
                    <p className="text-xs text-gray-400">No overrides. Default brand applies to all sessions.</p>
                )}
                {overrides.map((override, i) => (
                    <div key={i} className="grid grid-cols-4 gap-2 mb-2 items-end">
                        <Select label="" options={SESSION_TYPES} value={override.session_type} onChange={(e) => updateOverride(i, 'session_type', e.target.value)} />
                        <Input placeholder="Name" value={override.name || ''} onChange={(e) => updateOverride(i, 'name', e.target.value)} />
                        <Select label="" options={BRAND_ICONS} value={override.icon || 'atom'} onChange={(e) => updateOverride(i, 'icon', e.target.value)} />
                        <div className="flex items-center gap-1">
                            <input type="color" value={override.color || '#4D4D4D'} onChange={(e) => updateOverride(i, 'color', e.target.value)} className="w-7 h-7 rounded cursor-pointer" />
                            <button type="button" onClick={() => removeOverride(i)} className="text-xs text-red-500 hover:text-red-700 ml-1">x</button>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
}
